/**
 * RFC 9457 problem details — the error shape the Docsight API returns
 * (`application/problem+json`).
 */
export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  [key: string]: unknown;
}

/** The server answered, but with a non-2xx status. */
export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetails | null;
  readonly url: string;

  constructor(message: string, status: number, problem: ProblemDetails | null, url: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
    this.url = url;
  }
}

/** The request exceeded its deadline and was aborted by the client. */
export class ApiTimeoutError extends Error {
  readonly timeoutMs: number;
  readonly url: string;

  constructor(timeoutMs: number, url: string) {
    super(`Request to ${url} timed out after ${String(timeoutMs)}ms`);
    this.name = 'ApiTimeoutError';
    this.timeoutMs = timeoutMs;
    this.url = url;
  }
}

/** The request never reached the server (DNS, TLS, offline, CORS …). */
export class ApiNetworkError extends Error {
  readonly url: string;

  constructor(url: string, cause: unknown) {
    super(`Request to ${url} failed before a response was received`, { cause });
    this.name = 'ApiNetworkError';
    this.url = url;
  }
}

/** Narrow an unknown decoded body to problem details. */
export function toProblemDetails(body: unknown): ProblemDetails | null {
  if (typeof body !== 'object' || body === null || Array.isArray(body)) return null;
  return body as ProblemDetails;
}
